import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Form, Card, Row, Col, Button, Alert, Modal } from 'react-bootstrap';
import InputMaskLib from 'react-input-mask';
import { toast } from 'react-toastify';
import useSWR, { mutate } from 'swr';
import ActivitiesModal from '../../components/agenda/Modals/ActivitiesModal';
import getPacienteById from '../../services/paciente';
import api from '../../services/api';
import { validarPacienteForm } from './pacienteSchema';
import interpretarErroPaciente from './errosValidacaoApi';
import { cpfParaConsulta, outrosPacientesComCpf } from './cpfDuplicado';
import styles from '../../styles.module.css';
import { changeColor } from '../../util/agendaHelpers';
import ReceituarioMedico from '../../pages/receita/ReceituarioMedico';
import { formatarHora } from '../../util/utils';
import useAtividades from '../../hooks/useAtividades';
import useLaudoHandlers from '../../hooks/useLaudoHandlers';

const OBJETO_PACIENTE = {
  CODPAC: '',
  NOMEPAC: '',
  DTNPAC: '',
  CELULARPAC: '',
  ENDERECO_NR: '',
  BAIRROPAC: '',
  ENDRESPAC: '',
  ESTADOPAC: '',
  CIDADEPAC: '',
  FONERESPAC: '',
  FONECOMPAC: '',
  INFORMACAO: '',
  EMPRESA: '',
  PROFISSPAC: '',
  SEXOPAC: '',
  LOCNASCPAC: '',
  SETORPAC: '',
  CEPPAC: '',
  ENDMARPAC: '',
  ENDERECO_COMPL: '',
  CODFUNC: '',
  CODINTERNADO: '',
  VALIDADE: '',
  FONERECPAC: '',
  RAMALPAC: '',
  ESTCIVIL: '',
  NACIONPAC: '',
  CORPAC: '',
  UPDATED_AT: '',
  DATACADPAC: '',
  DATAPAC: '',
};

const formatDtnPacFromIso = iso => {
  if (!iso) return '';
  const [date] = iso.split('T');
  const [yyyy, mm, dd] = date.split('-');
  if (!yyyy || !mm || !dd) return '';
  return `${dd}/${mm}/${yyyy}`;
};

const CAMPOS_UPDATE_PACIENTE = [
  'NOMEPAC',
  'LOCNASCPAC',
  'ENDMARPAC',
  'DTNPAC',
  'VALIDADE',
  'EMPRESA',
  'CEPPAC',
  'ENDRESPAC',
  'ENDERECO_NR',
  'ENDERECO_COMPL',
  'BAIRROPAC',
  'CIDADEPAC',
  'ESTADOPAC',
  'FONERESPAC',
  'FONECOMPAC',
  'FONERECPAC',
  'RAMALPAC',
  'CELULARPAC',
  'EMAIL_PAC',
  'INFORMACAO',
  'PROFISSPAC',
  'SEXOPAC',
  'SETORPAC',
  'CODINTERNADO',
  'ESTCIVIL',
  'NACIONPAC',
  'CORPAC',
];

const valorCampo = value => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const buildPacienteUpdatePayload = (current, baseline) => {
  const payload = { CODPAC: current.CODPAC };
  CAMPOS_UPDATE_PACIENTE.forEach(key => {
    if (valorCampo(current[key]) !== valorCampo(baseline && baseline[key])) {
      payload[key] = current[key] ?? '';
    }
  });
  return payload;
};

const mapPacienteFromApi = record => ({
  CODPAC: record.CODPAC || '',
  NOMEPAC: record.NOMEPAC || '',
  DTNPAC: formatDtnPacFromIso(record.DTNPAC),
  CELULARPAC: record.CELULARPAC || '',
  ENDERECO_NR: record.ENDERECO_NR || '',
  BAIRROPAC: record.BAIRROPAC || '',
  ENDRESPAC: record.ENDRESPAC || '',
  ESTADOPAC: record.ESTADOPAC || '',
  CIDADEPAC: record.CIDADEPAC || '',
  FONERESPAC: record.FONERESPAC || '',
  FONECOMPAC: record.FONECOMPAC || '',
  INFORMACAO: record.INFORMACAO || '',
  EMPRESA: record.EMPRESA || '',
  PROFISSPAC: record.PROFISSPAC || '',
  SEXOPAC: record.SEXOPAC || '',
  LOCNASCPAC: record.LOCNASCPAC || '',
  SETORPAC: record.SETORPAC || '',
  CEPPAC: record.CEPPAC || '',
  ENDMARPAC: record.ENDMARPAC || '',
  ENDERECO_COMPL: record.ENDERECO_COMPL || '',
  DATAPAC: record.DATAPAC || '',
  DATACADPAC: record.DATACADPAC || '',
  UPDATED_AT: record.UPDATED_AT || '',
  CODFUNC: record.CODFUNC || '',
  NOMEFUNC: record.NOMEFUNC || '',
  CODINTERNADO: record.CODINTERNADO || '',
  VALIDADE: record.VALIDADE || '',
  FONERECPAC: record.FONERECPAC || '',
  RAMALPAC: record.RAMALPAC || '',
  ESTCIVIL: record.ESTCIVIL || '',
  NACIONPAC: record.NACIONPAC || '',
  CORPAC: record.CORPAC || '',
});

// TODO: separar entre update e create. já ta com 1000+ linhas. diminuir com componentes separados.
// aqui era pra ser só info, já tem agendar, criar... rever!
const PacienteInfo = React.memo(
  ({
    codPac = null,
    isCreation = false,
    isAgendamento,
    onAgendar,
    onCreated,
    onNovo,
    loggedUser,
    onFaturarAgenda,
    onAbrirPaciente,
  }) => {
    const loggedUserId = loggedUser?.idFunc || loggedUser?.codFunc;
    const [codPacAberto, setCodPacAberto] = useState(null);
    const codPacEfetivo = codPacAberto || codPac;

    const {
      loadingAtividadeId,
      handleImprimir,
      handleImprimirTXT,
      handleEspelhoDoppler,
      handleEspelhoFisio,
      handleEspelhoFarmaco,
      handleImprimirEspelho,
    } = useLaudoHandlers();
    const {
      atividades,
      loadingAtividades,
      showAtividadesModal,
      openAtividades,
      closeAtividadesModal,
      atividadesActions, // <-- Extraído de forma explícita!
    } = useAtividades({
      idMedico: loggedUserId,
      medico: loggedUser,
      syncUrl: false,
    });
    const { data, error, isValidating } = useSWR(
      codPacEfetivo ? ['paciente', codPacEfetivo] : null,
      () => getPacienteById(codPacEfetivo)
    );
    const [showReceitaModal, setShowReceitaModal] = useState(false);
    const [pacientesDuplicados, setPacientesDuplicados] = useState([]);
    const [paciente, setPaciente] = useState({ ...OBJETO_PACIENTE });
    const [formErrors, setFormErrors] = useState({});
    const [errosSalvar, setErrosSalvar] = useState([]);
    const [agendaPac, setAgendaPac] = useState([]);
    const numInput = useRef(null);
    const cpfConsultaRef = useRef('');
    const cpfConsultadoRef = useRef('');

    const atividadesActionsCompleto = useMemo(
      () => ({
        ...(atividadesActions || {}),
        setShowReceitaModal,
        loadingAtividadeId,
        handleImprimir,
        handleImprimirTXT,
        handleEspelhoDoppler,
        handleEspelhoFisio,
        handleEspelhoFarmaco,
        handleImprimirEspelho,
      }),
      [
        atividadesActions,
        loadingAtividadeId,
        handleImprimir,
        handleImprimirTXT,
        handleEspelhoDoppler,
        handleEspelhoFisio,
        handleEspelhoFarmaco,
        handleImprimirEspelho,
      ]
    );

    useEffect(() => {
      async function loadAgendaPac() {
        try {
          if (!codPacEfetivo || codPacEfetivo === 1) return;
          const { data } = await api.get(
            `pacientes/${codPacEfetivo}/agendas?order=DESC`
          );
          setAgendaPac(data);
        } catch (error) {
          toast.error(
            error?.response?.data?.error || 'Erro ao carregar agenda'
          );
        }
      }
      loadAgendaPac();
    }, [codPacEfetivo]);

    useEffect(() => {
      setCodPacAberto(null);
    }, [codPac]);

    // Sempre que data/codPac mudar, reseta o estado local
    useEffect(() => {
      if (isCreation && !codPacAberto) {
        setPaciente({ ...OBJETO_PACIENTE });
        return;
      }
      if (!data || data.length === 0) return;
      const registro = data[0];
      if (codPacEfetivo && String(registro.CODPAC) !== String(codPacEfetivo)) {
        return;
      }
      setPaciente(mapPacienteFromApi(registro));
    }, [isCreation, data, codPacEfetivo, codPacAberto]);

    // Derived state: current paciente (edited or from API)
    const currentPaciente = useMemo(() => {
      // If paciente has been edited (has CODPAC), use edited version
      if (paciente.CODPAC) return paciente;

      if (data && data.length > 0) {
        return mapPacienteFromApi(data[0]);
      }

      return paciente;
    }, [paciente, data]);

    const limparErro = campo => {
      setFormErrors(prev => {
        if (!prev[campo]) return prev;
        const next = { ...prev };
        delete next[campo];
        return next;
      });
    };

    const rejeitarFormularioInvalido = () => {
      const erros = validarPacienteForm(currentPaciente);
      if (!Object.keys(erros).length) {
        setFormErrors({});
        return false;
      }
      setFormErrors(erros);
      setErrosSalvar([]);
      return true;
    };

    const registrarErroSalvar = (error, fallback) => {
      const { porCampo, outras } = interpretarErroPaciente(error, fallback);
      setFormErrors(porCampo);
      setErrosSalvar(outras);
    };

    async function handleUpdatePaciente() {
      if (currentPaciente && currentPaciente.CODPAC === 1) {
        toast.error('Paciente 1 - NULO não pode ser editado!');
        return;
      }

      if (rejeitarFormularioInvalido()) return;

      const baseline =
        data && data.length > 0 ? mapPacienteFromApi(data[0]) : null;
      const payload = buildPacienteUpdatePayload(currentPaciente, baseline);
      if (Object.keys(payload).length === 1) {
        toast.info('Nenhuma alteração para salvar.');
        return;
      }

      try {
        await api.put(`/paciente`, payload);
        setFormErrors({});
        setErrosSalvar([]);
        toast.success('Paciente atualizado com sucesso!');
        if (currentPaciente && currentPaciente.CODPAC) {
          mutate(['paciente', currentPaciente.CODPAC]); // revalida o paciente atual
        }
      } catch (error) {
        registrarErroSalvar(error, 'Erro ao atualizar paciente!');
      }
    }

    async function createNewPatient() {
      if (rejeitarFormularioInvalido()) return;

      try {
        const res = await api.post(`/pacientes`, currentPaciente);
        const novoPaciente = res && res.data && res.data.paciente;
        if (novoPaciente?.CODPAC && typeof onCreated === 'function') {
          onCreated(novoPaciente);
        }
        setFormErrors({});
        setErrosSalvar([]);
        toast.success('Paciente cadastrado com sucesso!');
      } catch (error) {
        registrarErroSalvar(error, 'Erro ao cadastrar paciente!');
      }
    }

    const handleChange = e => {
      const { name, value } = e.target;
      setPaciente(prev => ({
        ...prev,
        [name]: value,
      }));
      if (name === 'ENDMARPAC') {
        cpfConsultaRef.current = '';
        cpfConsultadoRef.current = '';
      }
      limparErro(name);
      setErrosSalvar(prev => (prev.length ? [] : prev));
    };

    const handleFecharDuplicata = () => {
      setPacientesDuplicados([]);
    };

    const handleAbrirPacienteDuplicado = pac => {
      setPacientesDuplicados([]);
      setCodPacAberto(pac.CODPAC);
      setPaciente(mapPacienteFromApi(pac));
      if (typeof onAbrirPaciente === 'function') onAbrirPaciente(pac);
    };

    const handleCpfBlur = async event => {
      const digitos = cpfParaConsulta(event.target.value);
      const codPacAtual = currentPaciente && currentPaciente.CODPAC;
      if (!digitos || digitos === cpfConsultadoRef.current) return;

      cpfConsultaRef.current = digitos;
      try {
        const { data: encontrados } = await api.get('/pacientes', {
          params: { cpf: digitos },
        });
        if (cpfConsultaRef.current !== digitos) return;
        cpfConsultadoRef.current = digitos;
        setPacientesDuplicados(outrosPacientesComCpf(encontrados, codPacAtual));
      } catch (error) {
        if (cpfConsultaRef.current !== digitos) return;
        toast.error(
          error?.response?.data?.error ||
          'Não foi possível verificar se o CPF já está cadastrado.'
        );
      }
    };

    // Função para avançar para o próximo campo ao pressionar ENTER
    // isso deve ser um hook
    const handleEnter = e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const { form } = e.target;
        const index = Array.prototype.indexOf.call(form, e.target);
        let next = null;
        // Busca próximo input visível, habilitado e que não seja radio/checkbox
        for (let i = index + 1; i < form.length; i++) {
          if (
            (form[i].tagName === 'INPUT' &&
              form[i].type !== 'radio' &&
              form[i].type !== 'checkbox') ||
            form[i].tagName === 'TEXTAREA' ||
            form[i].tagName === 'SELECT'
          ) {
            if (
              !form[i].disabled &&
              form[i].type !== 'hidden' &&
              form[i].offsetParent !== null
            ) {
              next = form[i];
              break;
            }
          }
        }
        if (next) next.focus();
      }
    };

    // TODO: isso aqui é usado em um monte de lugar. tem que centralizar e parar de repetir!
    const handleCep = () => {
      fetch(
        `https://viacep.com.br/ws/${currentPaciente &&
        currentPaciente.CEPPAC}/json/`
      )
        .then(response => response.json())
        .then(data => {
          setPaciente({
            ...currentPaciente,
            ENDRESPAC: (data.logradouro && data.logradouro.toUpperCase()) || '',
            BAIRROPAC: (data.bairro && data.bairro.toUpperCase()) || '',
            CIDADEPAC: (data.localidade && data.localidade.toUpperCase()) || '',
            ESTADOPAC: (data.uf && data.uf.toUpperCase()) || '',
          });
          if (numInput.current) {
            numInput.current.focus();
            if (typeof numInput.current.select === 'function')
              numInput.current.select();
          }
        })
        .catch(() => toast.error('Erro ao buscar CEP!'));
    };

    const mensagensAlerta = [...errosSalvar, ...Object.values(formErrors)];

    const alertaErros =
      mensagensAlerta.length > 0 ? (
        <Alert
          variant="danger"
          dismissible
          onClose={() => {
            setErrosSalvar([]);
            setFormErrors({});
          }}
          className="text-left mb-3"
        >
          <ul className="mb-0 pl-3">
            {mensagensAlerta.map(mensagem => (
              <li key={mensagem}>{mensagem}</li>
            ))}
          </ul>
        </Alert>
      ) : null;

    return (
      <>
        <Card className="mt-2">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <Form.Label className="mb-0 mr-2" style={{ fontWeight: 500 }}>
              PAC: {currentPaciente?.CODPAC || ''}
            </Form.Label>
            <span style={{ margin: '0 auto', fontWeight: 600, fontSize: 26 }}>
              PACIENTE
            </span>
            {/* Direita: Datas */}
            <div style={{ right: 0, minWidth: 180, fontSize: 12 }}>
              <div style={{ fontWeight: 'bold', color: '#007bff' }}>
                <span>Criado:</span>{' '}
                {currentPaciente?.DATACADPAC
                  ? new Date(currentPaciente.DATACADPAC).toLocaleString('pt-BR')
                  : '--'}
              </div>
              <div style={{ fontWeight: 'bold', color: 'green' }}>
                <span>Atualizado:</span>{' '}
                {currentPaciente?.DATAPAC
                  ? new Date(currentPaciente.DATAPAC).toLocaleString('pt-BR')
                  : '--'}
              </div>
            </div>
          </Card.Header>
          {isValidating && !data ? (
            <Alert variant="info text-center mt-3 w-50 mx-auto">
              Carregando dados do paciente...
            </Alert>
          ) : error ? (
            <Alert variant="danger text-center mt-3 w-50 mx-auto">
              Erro ao buscar paciente!
            </Alert>
          ) : (
            <>
              <Card.Body>
                <Form>
                  <div>
                    <Row>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group className={styles.tightGroup}>
                          <Form.Label className={styles.tightLabel}>
                            Func
                          </Form.Label>
                          <Form.Control
                            value={
                              currentPaciente?.NOMEFUNC
                                ? currentPaciente.NOMEFUNC.split(' ')[0]
                                : ''
                            }
                            name="CODFUNC"
                            readOnly
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6} className={styles.tightCol}>
                        <Form.Group
                          controlId="formNome"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Nome
                          </Form.Label>
                          <Form.Control
                            style={{
                              textTransform: 'uppercase',
                              borderColor: 'green',
                              backgroundColor: 'rgba(194, 253, 149, 0.3)',
                            }}
                            type="text"
                            value={currentPaciente?.NOMEPAC || ''}
                            name="NOMEPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                            isInvalid={Boolean(formErrors.NOMEPAC)}
                          />
                          {formErrors.NOMEPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.NOMEPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formCPF"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            CPF
                          </Form.Label>
                          <InputMaskLib
                            mask="999.999.999-99"
                            value={currentPaciente?.ENDMARPAC || ''}
                            onChange={e => handleChange(e)}
                            onBlur={handleCpfBlur}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                style={{
                                  textTransform: 'uppercase',
                                  borderColor: 'green',
                                  backgroundColor: 'rgba(194, 253, 149, 0.3)',
                                }}
                                name="ENDMARPAC"
                                onKeyDown={handleEnter}
                                className={styles.tightInput}
                                isInvalid={Boolean(formErrors.ENDMARPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.ENDMARPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.ENDMARPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formSexo"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Sexo
                          </Form.Label>
                          <Col className={styles.tightCol}>
                            <Form.Check
                              style={{
                                borderColor: 'green',
                                backgroundColor: 'rgba(194, 253, 149, 0.3)',
                              }}
                              inline
                              label="M"
                              type="radio"
                              id="radioMasculino"
                              checked={currentPaciente?.SEXOPAC === 'M'}
                              name="SEXOPAC"
                              onChange={() =>
                                setPaciente({
                                  ...currentPaciente,
                                  SEXOPAC: 'M',
                                })
                              }
                            />
                            <Form.Check
                              inline
                              style={{
                                borderColor: 'green',
                                backgroundColor: 'rgba(194, 253, 149, 0.3)',
                              }}
                              label="F"
                              type="radio"
                              id="radioFeminino"
                              name="SEXOPAC"
                              checked={currentPaciente?.SEXOPAC === 'F'}
                              onChange={() =>
                                setPaciente({
                                  ...currentPaciente,
                                  SEXOPAC: 'F',
                                })
                              }
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Endereço Rows */}
                    <Row>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formDtNasc"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Data Nasc
                          </Form.Label>
                          <InputMaskLib
                            mask="99/99/9999"
                            value={currentPaciente?.DTNPAC || ''}
                            onChange={e => {
                              setPaciente({
                                ...currentPaciente,
                                DTNPAC: e.target.value,
                              });
                              limparErro('DTNPAC');
                            }}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                style={{
                                  textTransform: 'uppercase',
                                  borderColor: 'green',
                                  backgroundColor: 'rgba(194, 253, 149, 0.3)',
                                }}
                                name="DTNPAC"
                                onKeyDown={handleEnter}
                                className={styles.tightInput}
                                isInvalid={Boolean(formErrors.DTNPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.DTNPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.DTNPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={1} className={styles.tightCol}>
                        <Form.Group
                          controlId="formConv"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Conv
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.CODINTERNADO || ''}
                            name="CODINTERNADO"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={3} className={styles.tightCol}>
                        <Form.Group
                          controlId="formCarteirinha"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Nr Carteirinha
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.LOCNASCPAC || ''}
                            name="LOCNASCPAC"
                            onChange={e => handleChange(e)}
                            maxLength={20}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={1} className={styles.tightCol}>
                        <Form.Group
                          controlId="formValidade"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Validade
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.VALIDADE || ''}
                            name="VALIDADE"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={3} className={styles.tightCol}>
                        <Form.Group
                          controlId="formProfiss"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Profissão
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.PROFISSPAC || ''}
                            name="PROFISSPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formEmpresa"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Empresa
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.EMPRESA || ''}
                            name="EMPRESA"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Contato */}
                  <div>
                    <Row>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formEnd"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            CEP
                          </Form.Label>
                          <InputMaskLib
                            mask="99999-999"
                            value={currentPaciente?.CEPPAC || ''}
                            onChange={e => handleChange(e)}
                            onBlur={handleCep}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                style={{
                                  borderColor: 'green',
                                  backgroundColor: 'rgba(194, 253, 149, 0.3)',
                                }}
                                name="CEPPAC"
                                className={styles.tightInput}
                                onKeyDown={handleEnter}
                                isInvalid={Boolean(formErrors.CEPPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.CEPPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.CEPPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={6} className={styles.tightCol}>
                        <Form.Group
                          controlId="formCidade"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Endereço
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.ENDRESPAC || ''}
                            name="ENDRESPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                            isInvalid={Boolean(formErrors.ENDRESPAC)}
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group className={styles.tightGroup}>
                          <Form.Label className={styles.tightLabel}>
                            Número
                          </Form.Label>
                          <Form.Control
                            onChange={e => handleChange(e)}
                            name="ENDERECO_NR"
                            value={currentPaciente?.ENDERECO_NR || ''}
                            ref={numInput}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                            isInvalid={Boolean(formErrors.ENDERECO_NR)}
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group className={styles.tightGroup}>
                          <Form.Label className={styles.tightLabel}>
                            Complemento
                          </Form.Label>
                          <Form.Control
                            onChange={e => handleChange(e)}
                            name="ENDERECO_COMPL"
                            value={currentPaciente?.ENDERECO_COMPL || ''}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col sm={4} className={styles.tightCol}>
                        <Form.Group
                          controlId="formCidade"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Cidade
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.CIDADEPAC || ''}
                            name="CIDADEPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                            isInvalid={Boolean(formErrors.CIDADEPAC)}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={4} className={styles.tightCol}>
                        <Form.Group
                          controlId="formBairro"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Bairro
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.BAIRROPAC || ''}
                            name="BAIRROPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                            isInvalid={Boolean(formErrors.BAIRROPAC)}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={4} className={styles.tightCol}>
                        <Form.Group
                          controlId="formEstado"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Estado
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.ESTADOPAC || ''}
                            name="ESTADOPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                            isInvalid={Boolean(formErrors.ESTADOPAC)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formFoneRes"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Fone Residencial
                          </Form.Label>
                          <InputMaskLib
                            mask={
                              (currentPaciente?.FONERESPAC || '').replace(
                                /\D/g,
                                ''
                              ).length > 10
                                ? '(999)99999-9999'
                                : '(999)9999-9999'
                            }
                            value={currentPaciente?.FONERESPAC || ''}
                            onChange={e => handleChange(e)}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                className={styles.tightInput}
                                name="FONERESPAC"
                                onKeyDown={handleEnter}
                                isInvalid={Boolean(formErrors.FONERESPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.FONERESPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.FONERESPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formFoneComer"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Fone Comercial
                          </Form.Label>
                          <InputMaskLib
                            mask="(999)99999-9999"
                            value={currentPaciente?.FONECOMPAC || ''}
                            onChange={e => handleChange(e)}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                className={styles.tightInput}
                                name="FONECOMPAC"
                                onKeyDown={handleEnter}
                                isInvalid={Boolean(formErrors.FONECOMPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.FONECOMPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.FONECOMPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formFoneAgendamento"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Fone Agendamento
                          </Form.Label>
                          <InputMaskLib
                            mask="(999)99999-9999"
                            value={currentPaciente?.FONERECPAC || ''}
                            onChange={e => handleChange(e)}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                style={{
                                  borderColor: 'green',
                                  backgroundColor: 'rgb(236, 253, 48)',
                                }}
                                className={styles.tightInput}
                                name="FONERECPAC"
                                onKeyDown={handleEnter}
                                isInvalid={Boolean(formErrors.FONERECPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.FONERECPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.FONERECPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formFoneObs"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Obs Fone
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={currentPaciente?.RAMALPAC || ''}
                            name="RAMALPAC"
                            onChange={e => handleChange(e)}
                            onKeyDown={handleEnter}
                            className={styles.tightInput}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={4} className={styles.tightCol}>
                        <Form.Group
                          controlId="foneCelular"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Fone WhatsApp 🟢
                          </Form.Label>
                          <InputMaskLib
                            mask="(999)99999-9999"
                            value={currentPaciente?.CELULARPAC || ''}
                            onChange={e => handleChange(e)}
                          >
                            {inputProps => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                className={styles.tightInput}
                                style={{
                                  borderColor: 'green',
                                  backgroundColor: 'rgba(22, 252, 6, 0.69)',
                                }}
                                name="CELULARPAC"
                                onKeyDown={handleEnter}
                                isInvalid={Boolean(formErrors.CELULARPAC)}
                              />
                            )}
                          </InputMaskLib>
                          {formErrors.CELULARPAC && (
                            <Form.Text className="text-danger">
                              {formErrors.CELULARPAC}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                  {/* Observações */}
                  <div>
                    <Row>
                      <Col sm={3} className={styles.tightCol}>
                        <Form.Group
                          controlId="formEstadoCivil"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Estado Civil
                          </Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            name="ESTCIVIL"
                            onChange={e => handleChange(e)}
                            value={currentPaciente?.ESTCIVIL || ''}
                          >
                            <option value="" disabled>
                              Estado Civil
                            </option>
                            {['SOLTEIRO', 'CASADO', 'DIVORCIADO', 'VIÚVO'].map(
                              option => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              )
                            )}
                          </Form.Control>
                        </Form.Group>
                      </Col>{' '}
                      <Col sm={2} className={styles.tightCol}>
                        <Form.Group
                          controlId="formCor"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Cor
                          </Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            name="CORPAC"
                            onChange={e => handleChange(e)}
                            value={currentPaciente?.CORPAC || ''}
                          >
                            <option value="" disabled>
                              {currentPaciente?.CORPAC || 'Cor'}
                            </option>
                            {['BRANCA', 'PRETA', 'PARDA', 'AMARELA'].map(
                              option => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              )
                            )}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={3} className={styles.tightCol}>
                        <Form.Group
                          controlId="formNacionalidade"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Nacionalidade
                          </Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            name="NACIONPAC"
                            onChange={e => handleChange(e)}
                            value={currentPaciente?.NACIONPAC || ''}
                          >
                            <option value="" disabled>
                              {currentPaciente?.NACIONPAC || 'Nacionalidade'}
                            </option>
                            {['BRASILEIRA', 'ESTRANGEIRA'].map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={4} className={styles.tightCol}>
                        <Form.Group
                          controlId="formStatus"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Status
                          </Form.Label>
                          <Col className={styles.tightCol}>
                            <Form.Check
                              inline
                              className="my-0 mr-1"
                              style={{
                                borderColor: 'green',
                                backgroundColor: 'rgba(194, 253, 149, 0.3)',
                              }}
                              label="Ativo"
                              type="radio"
                              id="radioAtivo"
                              checked={currentPaciente?.SETORPAC === 1}
                              name="SETORPAC"
                              onChange={() =>
                                setPaciente({ ...currentPaciente, SETORPAC: 1 })
                              }
                            />
                            <Form.Check
                              inline
                              style={{
                                borderColor: 'green',
                                backgroundColor: 'rgba(194, 253, 149, 0.3)',
                              }}
                              label="Inativo"
                              type="radio"
                              id="radioInativo"
                              name="SETORPAC"
                              checked={currentPaciente?.SETORPAC === 0}
                              onChange={() =>
                                setPaciente({ ...currentPaciente, SETORPAC: 0 })
                              }
                            />
                            <Form.Check
                              inline
                              style={{
                                borderColor: 'green',
                                backgroundColor: 'rgba(194, 253, 149, 0.3)',
                              }}
                              label="Mudou-se"
                              type="radio"
                              id="radioMudouSe"
                              name="SETORPAC"
                              checked={currentPaciente?.SETORPAC === 2}
                              onChange={() =>
                                setPaciente({ ...currentPaciente, SETORPAC: 2 })
                              }
                            />
                            <Form.Check
                              inline
                              style={{
                                borderColor: 'red',
                                backgroundColor: 'rgb(248, 12, 4)',
                              }}
                              label="Pendente"
                              type="radio"
                              id="radioPendente"
                              name="SETORPAC"
                              checked={currentPaciente?.SETORPAC === 3}
                              onChange={() =>
                                setPaciente({ ...currentPaciente, SETORPAC: 3 })
                              }
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={6} className={styles.tightCol}>
                        <Form.Group
                          controlId="formObs"
                          className={styles.tightGroup}
                        >
                          <Form.Label className={styles.tightLabel}>
                            Observações
                          </Form.Label>
                          <Form.Control
                            style={{ color: 'red' }}
                            as="textarea"
                            rows={8}
                            value={currentPaciente?.INFORMACAO || ''}
                            name="INFORMACAO"
                            onChange={e => handleChange(e)}
                            // onKeyDown={handleEnter}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                          <table
                            className="table table-sm table-bordered mt-2"
                            style={{ fontSize: '0.65rem' }}
                          >
                            <thead
                              style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: '#effaf0',
                              }}
                            >
                              <tr>
                                {onFaturarAgenda && <th>Fat</th>}
                                <th>Agenda</th>
                                <th>Horário</th>
                                <th>Situação</th>
                                <th>Procedimento</th>
                                <th>Médico</th>
                              </tr>
                            </thead>
                            <tbody>
                              {agendaPac?.length > 0 ? (
                                agendaPac.map(ag => (
                                  <tr
                                    key={ag.codAgenda}
                                    style={changeColor(ag)}
                                  >
                                    {onFaturarAgenda && (
                                      <td onClick={e => e.stopPropagation()}>
                                        <Button
                                          size="sm"
                                          variant="outline-success"
                                          disabled={ag.situacao === 'Cancelado'}
                                          /* TODO: Rever esse objeto que vem do backend
                                          Refatoração: extrair handleSelectFaturamento
                                          para useFaturamento.js, já que hoje ele mistura lógica de
                                          agenda com faturamento.
                                          */
                                          onClick={() =>
                                            onFaturarAgenda({
                                              ATIVIDADE: ag.atividade,
                                              CODAGENDA: ag.codAgenda,
                                              CODCONV: ag.codConv,
                                              CODMED: ag.codMed,
                                              CODPAC: ag.codPac,
                                              CODPROCEDI: ag.codProcedi,
                                              DATAAGENDA: ag.dataAgenda,
                                              OBSERVACAO: ag.observacao,
                                            })
                                          }
                                        >
                                          $
                                        </Button>
                                      </td>
                                    )}
                                    <td>{ag.dataFormatada}</td>
                                    <td>{formatarHora(ag.horario)}</td>
                                    <td>{ag.situacao || ''}</td>
                                    <td>{ag.procedimento || ''}</td>
                                    <td>
                                      {ag.medico
                                        ? ag.medico
                                          .split(' ')
                                          .slice(0, 2)
                                          .join(' ')
                                        : 'ICM'}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={onFaturarAgenda ? 6 : 5}
                                    className={`text-center text-muted `}
                                    style={{
                                      fontStyle: 'italic',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      backgroundColor: '#d9fbda',
                                      border: '3px solid #016722',
                                      borderRadius: '5px',
                                    }}
                                  >
                                    Nenhuma agenda encontrada
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </Card.Body>
              <Card.Footer className="text-center">
                {alertaErros}
                {isCreation ? (
                  <>
                    <Button variant="success" onClick={createNewPatient}>
                      Gravar
                    </Button>
                    {typeof onNovo === 'function' && (
                      <Button className="ml-2" onClick={onNovo}>
                        Limpar
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button variant="success" onClick={handleUpdatePaciente}>
                      Atualizar
                    </Button>{' '}
                    {typeof onNovo === 'function' && (
                      <Button variant="warning" onClick={onNovo}>
                        Novo Paciente
                      </Button>
                    )}{' '}
                  </>
                )}
                {isAgendamento && (
                  <Button
                    variant="success"
                    disabled={!currentPaciente?.CODPAC}
                    onClick={() => {
                      if (onAgendar)
                        onAgendar(
                          currentPaciente?.CODPAC,
                          currentPaciente?.NOMEPAC
                        );
                    }}
                  >
                    AGENDAR
                  </Button>
                )}{' '}
                {/* Botão para acionar as atividades no formulário */}
                <Button
                  variant="info"
                  type="button"
                  onClick={() =>
                    openAtividades(
                      currentPaciente?.CODPAC,
                      currentPaciente?.NOMEPAC
                    )
                  }
                  disabled={!codPacEfetivo}
                >
                  Atividades
                </Button>
              </Card.Footer>
            </>
          )}
        </Card>

        <Modal
          show={showReceitaModal}
          onHide={() => setShowReceitaModal(false)}
          size="xl"
        >
          <Modal.Header className="font-weight-bold">
            RECEITUÁRIO MÉDICO
          </Modal.Header>
          <Modal.Body>
            <ReceituarioMedico
              closeModal={() => setShowReceitaModal(false)}
              codPaciente={paciente.CODPAC}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={pacientesDuplicados.length > 0}
          onHide={handleFecharDuplicata}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>CPF já cadastrado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning" className="mb-3">
              Este CPF já pertence a outro paciente. Confira os dados antes de
              gravar.
            </Alert>
            {pacientesDuplicados.map(pac => (
              <div key={pac.CODPAC} className="border rounded p-3 mb-3">
                <div className="h5 mb-3">{pac.NOMEPAC}</div>
                <Row>
                  <Col xs={6} className="mb-2">
                    <div className="text-muted small">Código</div>
                    <div>{pac.CODPAC}</div>
                  </Col>
                  <Col xs={6} className="mb-2">
                    <div className="text-muted small">CPF</div>
                    <div style={{ whiteSpace: 'nowrap' }}>{pac.ENDMARPAC}</div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted small">Celular</div>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {pac.CELULARPAC || '—'}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted small">Nascimento</div>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {pac.dataFormatada || pac.DTNPAC || '—'}
                    </div>
                  </Col>
                </Row>
                {pacientesDuplicados.length > 1 && (
                  <Button
                    className="mt-3"
                    block
                    onClick={() => handleAbrirPacienteDuplicado(pac)}
                  >
                    {isCreation
                      ? 'Abrir este cadastro'
                      : 'Abrir o outro paciente'}
                  </Button>
                )}
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleFecharDuplicata}>
              {isCreation ? 'Corrigir o CPF' : 'Continuar nesta ficha'}
            </Button>
            {pacientesDuplicados.length === 1 && (
              <Button
                variant="primary"
                onClick={() =>
                  handleAbrirPacienteDuplicado(pacientesDuplicados[0])
                }
              >
                {isCreation ? 'Abrir este cadastro' : 'Abrir o outro paciente'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {showAtividadesModal && (
          <ActivitiesModal
            show={showAtividadesModal}
            onHide={closeAtividadesModal}
            nomePac={currentPaciente?.NOMEPAC}
            atividades={atividades}
            loadingAtividades={loadingAtividades}
            actions={atividadesActionsCompleto}
          />
        )}
      </>
    );
  }
);

PacienteInfo.displayName = 'PacienteInfo';

export default PacienteInfo;
